import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { useApiUtils } from '@core/util/apiUtils';
import {
  useClienteInitialValue,
  useHabilitarDescontoInitialValue,
  useHabilitarFreteInitialValue,
  useHabilitarTipoDescontoInitialValue,
  useProdutosServicosInitialValue,
  useVendaInitialValue,
} from 'app/main/venda/components/modal/VendaInitialValues';
import { trataDadosFiscaisProduto } from 'app/main/venda/components/VendaProdutoServico/cadastro/atualizarAliquotaUtils';
import { cancelarAlert, confirmarAlert } from 'app/services/alerts';
import api from 'app/services/api/notaDevolucaoApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { FormikProvider, useFormik } from 'formik';
import React, { useContext, useEffect } from 'react';
import { FaTags } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { NotaFiscalContext } from '../../NotaFiscalContext';
import NotaDevolucaoForm from './NotaDevolucaoForm';
import schemaNotaDevolucao from './SchemaNotaDevolucao';

const NotaFiscalDevolucaoModal = () => {
  const [modal, setModal] = useContext(NotaFiscalContext).modal;
  const [notaAtual, setNotaAtual] = useContext(NotaFiscalContext).notaDevolucao;
  const [, setLoading] = useContext(NotaFiscalContext).loading;
  const [, setPendencias] = useContext(GlobalContext).pendencias;
  const [, setModalPendenciasFaturamento] = useContext(GlobalContext).modalPendenciasFaturamento;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  useEffect(() => {
    formik.setValues(initialValues);
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notaAtual]);

  const useFornecedorInitialValue = (fornecedor) => {
    return {
      id: fornecedor?.id || '',
      pessoa: {
        id: fornecedor?.pessoa?.id || '',
        nome: fornecedor?.pessoa?.nome || '',
      },
    };
  };

  const useCompraInitialValue = (compra) => {
    return {
      id: compra?.id || '',
      compraId: compra?.compraId,
      fornecedor: compra?.fornecedor || '',
    };
  };

  const initialValues = {
    id: notaAtual?.id || null,
    cliente: useClienteInitialValue(notaAtual?.cliente),
    fornecedor: useFornecedorInitialValue(notaAtual?.fornecedor),
    venda: useVendaInitialValue(notaAtual?.venda),
    compra: useCompraInitialValue(notaAtual?.compra),
    produtosServicos: useProdutosServicosInitialValue(notaAtual?.produtosServicos),
    operacao: notaAtual?.operacao || 'VENDA',
    chaveAcesso: notaAtual?.chaveAcesso,
    valorFrete: notaAtual?.valorFrete || '',
    tipoDesconto: notaAtual?.tipoDesconto || ` `,
    valorDesconto: notaAtual?.valorDesconto || '',
    valorTotal: notaAtual?.valorTotal || '',
    responsavelFrete: notaAtual?.responsavelFrete || ' ',
    transportadora: notaAtual?.transportadora,
    quantidadeVolumeTransportado: notaAtual?.quantidadeVolumeTransportado || '',
    tipoVolumeTrasportado: notaAtual?.tipoVolumeTrasportado || '',
    pesoBruto: notaAtual?.pesoBruto || '',
    pesoLiquido: notaAtual?.pesoLiquido || '',
    habilitarFrete: useHabilitarFreteInitialValue(notaAtual?.produtosServicos),
    habilitarTipoDesconto: useHabilitarTipoDescontoInitialValue(notaAtual?.produtosServicos),
    habilitarDesconto: useHabilitarDescontoInitialValue(notaAtual?.tipoDesconto),
  };

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const montaEntidadeParaEnvio = (notaDevolucao) => {
    const notaDevolucaoRequest = JSON.parse(JSON.stringify(notaDevolucao));

    notaDevolucaoRequest.clienteId = notaDevolucaoRequest.cliente?.id;
    notaDevolucaoRequest.fornecedorId = notaDevolucaoRequest.fornecedor?.id;
    notaDevolucaoRequest.compraId = notaDevolucaoRequest.compra?.id;
    notaDevolucaoRequest.vendaId = notaDevolucaoRequest.venda?.id;
    notaDevolucaoRequest.transportadoraId = notaDevolucaoRequest.transportadora?.id;

    if (notaDevolucaoRequest.produtosServicos)
      notaDevolucaoRequest.produtosServicos = notaDevolucaoRequest.produtosServicos.map(
        ({ id, fiscal, produtoServico: { produtoId, servicoId, issRetido, imobilizado }, quantidade, valor }) => ({
          id,
          produtoId,
          servicoId,
          quantidade,
          valor,
          issRetido,
          imobilizado,
          fiscal: trataDadosFiscaisProduto(fiscal),
        })
      );

    return notaDevolucaoRequest;
  };

  const submitForm = async (notaDevolucao) => {
    validarPendencias();
  };

  const formik = useFormik({
    validationSchema: schemaNotaDevolucao,
    initialValues,
    onSubmit: submitForm,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const close = () => {
    setNotaAtual({});
    formik.resetForm({});
    setModal('');
  };

  const handleClose = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const emitirNota = async () => {
    confirmarAlert({
      mensagem:
        'Tem certeza que deseja emitir a nota fiscal de devolução? Ao concordar, ela será emitida imediatamente. ',
      confirmar: async () => {
        if (!formik?.dirty) {
          return;
        }

        setLoading(true);

        const entidade = montaEntidadeParaEnvio(formik.values);

        try {
          eventReactGA('Nota fiscal', 'Emitir nota fiscal de devolução');
          const response = await api.cadastrar(entidade);
          if (response.data) {
            showSuccess('Nota de devolução salva com sucesso');
            close();
          }
        } catch (err) {
          apiUtils.handleApiError(err);
        } finally {
          setLoading(false);
          await api.atualizarHomol('CONCLUIDO');
        }
      },
    });
  };

  const validarPendencias = async () => {
    if (!formik?.dirty) {
      return;
    }

    setLoading(true);

    const entidade = montaEntidadeParaEnvio(formik.values);

    try {
      await api.validarPendencias(entidade).then((response) => {
        if (response.data && response.data.length > 0) {
          setPendencias(response.data);
          setModalPendenciasFaturamento('modalPendenciasFaturamento');
          setLoading(false);
        } else {
          setLoading(false);
          if (notaAtual?.id) {
            salvarDevolucao();
          } else {
            emitirNota();
          }
        }
      });
    } catch (err) {
      apiUtils.handleApiError(err);
      setLoading(false);
    }
  };

  const salvarDevolucao = async () => {
    if (!formik?.dirty) {
      return;
    }

    setLoading(true);

    const entidade = montaEntidadeParaEnvio(formik.values);

    try {
      eventReactGA('Nota fiscal', 'Cadastrar nota fiscal de devolução');
      const response = await api.cadastrar(entidade);
      if (response.data) {
        showSuccess('Nota de devolução salva com sucesso');
        close();
      }
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
      await api.atualizarHomol('CONCLUIDO');
    }
  };

  return (
    <SimpleDialog fullScreen open={modal === 'notaDevolucao'} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={() => {}}
          fullScreen
          header={{
            title: 'Nota de devolução',
            icon: <FaTags />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleClose}>
                  Cancelar
                </CancelButton>
                <SaveButton
                  color="buttonSave"
                  type="submit"
                  styles={undefined}
                  onClick={formik.handleSubmit}
                  variant="contained"
                >
                  {notaAtual?.id ? 'Salvar' : 'Emitir Nota Fiscal'}
                </SaveButton>
              </>
            ),
          }}
          content={
            <div className="h-full max-h-full w-full">
              <NotaDevolucaoForm />
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default NotaFiscalDevolucaoModal;
